import React, { Suspense } from 'react';
import random from 'lodash/random';
import sample from 'lodash/sample';
import { getLocalMerchantId } from 'components/settings/localStore';
import { getLoadingImagesFolder, preloadImage } from 'lib/imageHelpers';
import { Throbber } from 'lib/Throbber';
import { styled } from '@mui/material';
const CoffeeCup = React.lazy(() => import('./CoffeeCup'));
const CoffeeMug = React.lazy(() => import('./CoffeeMug'));
const Cupcake = React.lazy(() => import('./Cupcake'));
const Donut = React.lazy(() => import('./Donut'));
const PanFlip = React.lazy(() => import('./PanFlip'));
const Popsicle = React.lazy(() => import('./Popsicle'));

export enum ELoadingSVG {
    COFFEE_CUP = 'COFFEE_CUP',
    COFFEE_MUG = 'COFFEE_MUG',
    CUPCAKE = 'CUPCAKE',
    DONUT = 'DONUT',
    FRYING_PAN = 'FRYING_PAN',
    POPSICLE = 'POPSICLE'
}

interface IRandomLoadingSVGProps {
    enabledImages?: ELoadingSVG[];
    fallbackText: string;
}

const StyledLoadingImage = styled('img')(() => ({
    width: '50%',
    maxWidth: '800px'
}));

async function loadImage(merchantId: string, excludeArray: number[] = []): Promise<string | null> {
    const numbersArray = [1, 2, 3, 4, 5, 6].filter(number => !excludeArray.includes(number));
    const selectedNumber = numbersArray[random(1, numbersArray.length) - 1];
    const imageString = await preloadImage(`${getLoadingImagesFolder(merchantId)}/loading${selectedNumber}`);
    if (!imageString) {
        if (numbersArray.length === 1) {
            return null;
        }
        return loadImage(merchantId, [...excludeArray, selectedNumber]);
    }
    return imageString;
}

export const RandomLoading: React.FC<IRandomLoadingSVGProps> = props => {
    const { enabledImages, fallbackText } = props;
    const [merchantLoadingSrc, setMerchantLoadingSrc] = React.useState<string | null>();

    React.useEffect(() => {
        const merchantId = getLocalMerchantId();
        if (merchantId) {
            loadImage(merchantId).then(response => {
                setMerchantLoadingSrc(response);
            });
        }
    }, []);

    const randomSVG = React.useMemo(() => {
        if (!enabledImages) {
            return null;
        }
        const loadingSVGName = sample(enabledImages);
        switch (loadingSVGName) {
            case ELoadingSVG.COFFEE_CUP:
                return CoffeeCup;
            case ELoadingSVG.COFFEE_MUG:
                return CoffeeMug;
            case ELoadingSVG.CUPCAKE:
                return Cupcake;
            case ELoadingSVG.DONUT:
                return Donut;
            case ELoadingSVG.FRYING_PAN:
                return PanFlip;
            case ELoadingSVG.POPSICLE:
                return Popsicle;
            default:
                return null;
        }
    }, [enabledImages]);

    const loadingView = React.useMemo(() => {
        if (merchantLoadingSrc) {
            return <StyledLoadingImage src={merchantLoadingSrc} alt="loading" />;
        }
        if (merchantLoadingSrc !== undefined) {
            if (randomSVG) {
                return React.createElement(randomSVG, {
                    style: { width: '50%', maxWidth: '800px' }
                });
            }

            return <Throbber text={fallbackText} />;
        }

        return null;
    }, [fallbackText, merchantLoadingSrc, randomSVG]);

    return <Suspense fallback={<Throbber text={fallbackText} />}>{loadingView}</Suspense>;
};
